import { IonRouterOutlet } from "@ionic/react";
import { Redirect, Route, type RouteComponentProps } from "react-router-dom";
import { Routes } from "@/lib/router";
import { SettingsContactPage } from "@/routes/settings/contact";
import { SettingsNotificationsPage } from "@/routes/settings/notifications";
import { NotificationsDevicesPage } from "@/routes/settings/notifications/devices";
import { SettingsPersonalInformationPage } from "@/routes/settings/personal-information";
import { PrivacyPage } from "@/routes/settings/privacy";
import { ShiftPage } from "@/routes/shift/[id]";
import { AcceptInvitationToken } from "../auth/accept-invitation";
import { SettingsCustomFieldsPage } from "../settings/custom-fields";
import { AuthRouter } from "./auth.router";
import { TabRouter } from "./tab.router";

type RequireAuthOptions = {
  required?: boolean;
  authPath?: string;
  unauthPath?: string;
};

interface GlobalRouterProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  requireAuth: (
    component: <T extends object>(props: RouteComponentProps<T>) => JSX.Element,
    options?: RequireAuthOptions,
  ) => <T extends object>(props: RouteComponentProps<T>) => JSX.Element;
}

export function GlobalRouter({ isAuthenticated, isAdmin, requireAuth }: GlobalRouterProps) {
  return (
    <IonRouterOutlet id="global">
      {/* Redirect old accept-invitation route to new auth path for web compatibility */}
      <Route
        exact
        path="/accept-invitation/:token"
        render={({ match }) => <Redirect to={`/auth/accept-invitation/${match.params.token}`} />}
      />

      {/* Auth routes - outside of app context (includes accept-invitation) */}
      <Route path={Routes.Auth()} component={requireAuth(AuthRouter, { required: false })} />

      {/* Shift routes */}
      <Route exact path={Routes.Shift()} component={requireAuth(ShiftPage)} />

      {/* Settings routes */}
      <Route
        exact
        path={Routes.PersonalInformation()}
        component={requireAuth(SettingsPersonalInformationPage)}
      />
      <Route exact path={Routes.CustomFields()} component={requireAuth(SettingsCustomFieldsPage)} />
      <Route
        exact
        path={Routes.Notifications()}
        component={requireAuth(SettingsNotificationsPage)}
      />
      <Route
        exact
        path={Routes.NotificationsDevices()}
        component={requireAuth(NotificationsDevicesPage)}
      />
      <Route exact path={Routes.Contact()} component={requireAuth(SettingsContactPage)} />
      <Route exact path={Routes.Privacy()} component={requireAuth(PrivacyPage)} />

      {/* Main app with tabs - should match last */}
      <Route
        path={Routes.App()}
        component={requireAuth((props) => (
          <TabRouter {...props} isAdmin={isAdmin} />
        ))}
      />

      <Redirect exact from="/settings" to={Routes.Settings()} />

      <Redirect
        exact
        from="/"
        to={
          isAuthenticated
            ? isAdmin
              ? Routes.Conversations()
              : Routes.Projects()
            : Routes.AuthSignInPassword()
        }
      />
    </IonRouterOutlet>
  );
}
